type Difficulty = 'Easy' | 'Medium' | 'Hard';

interface Problem {
  id: string;
  title: string;
  difficulty: Difficulty;
  description: string;
  examples: Array<{
    input: string;
    output: string;
    explanation?: string;
  }>;
  boilerplateCode: Record<string, string>;
  starterCode: Record<string, string>;
  testCases: Array<{
    input: string;
    output: string;
  }>;
}

export const stackQueueProblems: Problem[] = [
  {
    id: 'stack-1',
    title: 'Valid Parentheses',
    difficulty: 'Easy',
    description: `Given a string \`s\` containing just the characters '(', ')', '{', '}', '[' and ']', determine if the input string is valid.

An input string is valid if:
1. Open brackets must be closed by the same type of brackets.
2. Open brackets must be closed in the correct order.
3. Every close bracket has a corresponding open bracket of the same type.`,
    examples: [
      {
        input: 's = "()"',
        output: 'true'
      },
      {
        input: 's = "()[]{}"',
        output: 'true'
      },
      {
        input: 's = "(]"',
        output: 'false'
      }
    ],
    boilerplateCode: {
      python: `import sys

# Parse input
input_data = sys.stdin.read().strip()
s = input_data

# Get result and format output
result = isValid(s)
print(result.lower())  # Convert to lowercase for consistency`,
      javascript: `// Parse input
const input = require('fs').readFileSync(0, 'utf-8').trim();
const s = input;

// Get result and format output
const result = isValid(s);
console.log(result ? 'true' : 'false');`,
      cpp: `#include <iostream>
#include <string>
#include <stack>

int main() {
    std::string s;
    std::getline(std::cin, s);
    
    bool result = isValid(s);
    std::cout << (result ? "true" : "false");
    return 0;
}`,
      java: `import java.util.Scanner;

public class Solution {
    public static void main(String[] args) {
        Scanner scanner = new Scanner(System.in);
        String s = scanner.nextLine();
        
        boolean result = isValid(s);
        System.out.println(result ? "true" : "false");
    }
}`
    },
    starterCode: {
      python: `def isValid(s):
    # Write your code here
    pass`,
      javascript: `function isValid(s) {
    // Write your code here
};`,
      cpp: `bool isValid(string s) {
    // Write your code here
}`,
      java: `public boolean isValid(String s) {
    // Write your code here
}`
    },
    testCases: [
      {
        input: '"()"',
        output: 'true'
      }
    ]
  },
  {
    id: 'queue-1',
    title: 'First Unique Character in a String',
    difficulty: 'Easy',
    description: `Given a string \`s\`, find the first non-repeating character in it and return its index. If it does not exist, return -1.`,
    examples: [
      {
        input: 's = "leetcode"',
        output: '0',
        explanation: 'The first non-repeating character is "l" with index 0.'
      },
      {
        input: 's = "loveleetcode"',
        output: '2',
        explanation: 'The first non-repeating character is "v" with index 2.'
      }
    ],
    boilerplateCode: {
      python: `import sys

# Parse input
input_data = sys.stdin.read().strip()
s = input_data

# Get result and format output
result = firstUniqChar(s)
print(result)`,
      javascript: `// Parse input
const input = require('fs').readFileSync(0, 'utf-8').trim();
const s = input;

// Get result and format output
const result = firstUniqChar(s);
console.log(result);`,
      cpp: `#include <iostream>
#include <string>
#include <unordered_map>

int main() {
    std::string s;
    std::getline(std::cin, s);
    
    int result = firstUniqChar(s);
    std::cout << result;
    return 0;
}`,
      java: `import java.util.Scanner;
import java.util.HashMap;

public class Solution {
    public static void main(String[] args) {
        Scanner scanner = new Scanner(System.in);
        String s = scanner.nextLine();
        
        int result = firstUniqChar(s);
        System.out.println(result);
    }
}`
    },
    starterCode: {
      python: `def firstUniqChar(s):
    # Write your code here
    pass`,
      javascript: `function firstUniqChar(s) {
    // Write your code here
};`,
      cpp: `int firstUniqChar(string s) {
    // Write your code here
}`,
      java: `public int firstUniqChar(String s) {
    // Write your code here
}`
    },
    testCases: [
      {
        input: '"leetcode"',
        output: '0'
      }
    ]
  },
  {
    id: 'stack-2',
    title: 'Min Stack',
    difficulty: 'Medium',
    description: `Design a stack that supports push, pop, top, and retrieving the minimum element in constant time.

Implement the \`MinStack\` class:
- \`MinStack()\` initializes the stack object.
- \`void push(int val)\` pushes the element val onto the stack.
- \`void pop()\` removes the element on the top of the stack.
- \`int top()\` gets the top element of the stack.
- \`int getMin()\` retrieves the minimum element in the stack.

You must implement a solution with \`O(1)\` time complexity for each function.`,
    examples: [
      {
        input: `["MinStack","push","push","push","getMin","pop","top","getMin"]
[[],[-2],[0],[-3],[],[],[],[]]`,
        output: '[null,null,null,null,-3,null,0,-2]',
        explanation: `
MinStack minStack = new MinStack();
minStack.push(-2);
minStack.push(0);
minStack.push(-3);
minStack.getMin(); // return -3
minStack.pop();
minStack.top();    // return 0
minStack.getMin(); // return -2`
      }
    ],
    boilerplateCode: {
      python: `import sys
import json

# Parse input
input_data = sys.stdin.read().strip().split('\n')
operations = json.loads(input_data[0])
values = json.loads(input_data[1])

# Get result and format output
results = []
minStack = MinStack()

for i in range(len(operations)):
    if operations[i] == 'push':
        minStack.push(values[i][0])
        results.append(None)
    elif operations[i] == 'pop':
        minStack.pop()
        results.append(None)
    elif operations[i] == 'top':
        results.append(minStack.top())
    elif operations[i] == 'getMin':
        results.append(minStack.getMin())

print(json.dumps(results))`,
      javascript: `// Parse input
const input = require('fs').readFileSync(0, 'utf-8').trim().split('\n');
const operations = JSON.parse(input[0]);
const values = JSON.parse(input[1]);

// Get result and format output
const results = [];
const minStack = new MinStack();

for (let i = 0; i < operations.length; i++) {
    if (operations[i] === 'push') {
        minStack.push(values[i][0]);
        results.push(null);
    } else if (operations[i] === 'pop') {
        minStack.pop();
        results.push(null);
    } else if (operations[i] === 'top') {
        results.push(minStack.top());
    } else if (operations[i] === 'getMin') {
        results.push(minStack.getMin());
    }
}

console.log(JSON.stringify(results));`,
      cpp: `#include <iostream>
#include <string>
#include <vector>
#include <sstream>
#include <iomanip>

int main() {
    std::string line1, line2;
    std::getline(std::cin, line1);
    std::getline(std::cin, line2);
    
    std::vector<std::string> operations;
    std::vector<std::vector<int>> values;
    
    // Parse operations
    std::stringstream ss1(line1);
    std::string op;
    while (std::getline(ss1, op, ',')) {
        operations.push_back(op);
    }
    
    // Parse values
    std::stringstream ss2(line2);
    std::string valStr;
    while (std::getline(ss2, valStr, ',')) {
        std::vector<int> val;
        std::stringstream ss3(valStr);
        int num;
        while (ss3 >> num) {
            val.push_back(num);
        }
        values.push_back(val);
    }
    
    MinStack minStack;
    std::vector<std::string> results;
    
    for (size_t i = 0; i < operations.size(); i++) {
        if (operations[i] == "push") {
            minStack.push(values[i][0]);
            results.push_back("null");
        } else if (operations[i] == "pop") {
            minStack.pop();
            results.push_back("null");
        } else if (operations[i] == "top") {
            int topVal = minStack.top();
            results.push_back(std::to_string(topVal));
        } else if (operations[i] == "getMin") {
            int minVal = minStack.getMin();
            results.push_back(std::to_string(minVal));
        }
    }
    
    std::cout << "[";
    for (size_t i = 0; i < results.size(); i++) {
        if (i > 0) std::cout << ",";
        std::cout << results[i];
    }
    std::cout << "]";
    return 0;
}`,
      java: `import java.util.Scanner;
import java.util.ArrayList;
import java.util.Arrays;

public class Solution {
    public static void main(String[] args) {
        Scanner scanner = new Scanner(System.in);
        String line1 = scanner.nextLine();
        String line2 = scanner.nextLine();
        
        String[] operations = line1.split(",");
        String[] valueStr = line2.split(",");
        
        ArrayList<MinStack> minStacks = new ArrayList<>();
        ArrayList<String> results = new ArrayList<>();
        
        for (int i = 0; i < operations.length; i++) {
            if (operations[i].equals("MinStack")) {
                minStacks.add(new MinStack());
                results.add("null");
            } else if (operations[i].equals("push")) {
                int val = Integer.parseInt(valueStr[i]);
                minStacks.get(minStacks.size() - 1).push(val);
                results.add("null");
            } else if (operations[i].equals("pop")) {
                minStacks.get(minStacks.size() - 1).pop();
                results.add("null");
            } else if (operations[i].equals("top")) {
                int topVal = minStacks.get(minStacks.size() - 1).top();
                results.add(String.valueOf(topVal));
            } else if (operations[i].equals("getMin")) {
                int minVal = minStacks.get(minStacks.size() - 1).getMin();
                results.add(String.valueOf(minVal));
            }
        }
        
        System.out.print("[");
        for (int i = 0; i < results.size(); i++) {
            if (i > 0) System.out.print(",");
            System.out.print(results.get(i));
        }
        System.out.print("]");
    }
}`
    },
    starterCode: {
      python: `class MinStack:
    def __init__(self):
        # Write your code here
        pass
        
    def push(self, val: int) -> None:
        # Write your code here
        pass
        
    def pop(self) -> None:
        # Write your code here
        pass
        
    def top(self) -> int:
        # Write your code here
        pass
        
    def getMin(self) -> int:
        # Write your code here
        pass`,
      javascript: `class MinStack {
    constructor() {
        // Write your code here
    }
    
    push(val) {
        // Write your code here
    }
    
    pop() {
        // Write your code here
    }
    
    top() {
        // Write your code here
    }
    
    getMin() {
        // Write your code here
    }
}`,
      cpp: `class MinStack {
public:
    MinStack() {
        // Write your code here
    }
    
    void push(int val) {
        // Write your code here
    }
    
    void pop() {
        // Write your code here
    }
    
    int top() {
        // Write your code here
    }
    
    int getMin() {
        // Write your code here
    }
};`,
      java: `class MinStack {
    public MinStack() {
        // Write your code here
    }
    
    public void push(int val) {
        // Write your code here
    }
    
    public void pop() {
        // Write your code here
    }
    
    public int top() {
        // Write your code here
    }
    
    public int getMin() {
        // Write your code here
    }
}`
    },
    testCases: [
      {
        input: '["MinStack","push","push","push","getMin","pop","top","getMin"]\n[[],[-2],[0],[-3],[],[],[],[]]',
        output: '[null,null,null,null,-3,null,0,-2]'
      }
    ]
  },
  {
    id: 'stack-3',
    title: 'Daily Temperatures',
    difficulty: 'Medium',
    description: `Given an array of integers \`temperatures\` represents the daily temperatures, return an array \`answer\` such that \`answer[i]\` is the number of days you have to wait after the \`i\`th day to get a warmer temperature.

If there is no future day for which this is possible, keep \`answer[i] == 0\` instead.`,
    examples: [
      {
        input: 'temperatures = [73,74,75,71,69,72,76,73]',
        output: '[1,1,4,2,1,1,0,0]',
        explanation: 'For each day, count the number of days until a warmer temperature.'
      },
      {
        input: 'temperatures = [30,40,50,60]',
        output: '[1,1,1,0]',
        explanation: 'Each day has a warmer temperature the next day except the last.'
      }
    ],
    boilerplateCode: {
      python: `import sys
import json

# Parse input
input_data = sys.stdin.read().strip()
temperatures = json.loads(input_data)

# Get result and format output
result = dailyTemperatures(temperatures)
print(json.dumps(result))`,
      javascript: `// Parse input
const input = require('fs').readFileSync(0, 'utf-8').trim();
const temperatures = JSON.parse(input);

// Get result and format output
const result = dailyTemperatures(temperatures);
console.log(JSON.stringify(result));`,
      cpp: `#include <iostream>
#include <string>
#include <vector>
#include <sstream>
#include <iomanip>

std::vector<int> parseArray(const std::string& input) {
    std::vector<int> result;
    std::stringstream ss(input.substr(1, input.size()-2));
    std::string item;
    while (std::getline(ss, item, ',')) {
        result.push_back(std::stoi(item));
    }
    return result;
}

int main() {
    std::string line;
    std::getline(std::cin, line);
    
    auto temperatures = parseArray(line);
    auto result = dailyTemperatures(temperatures);
    
    std::cout << "[";
    for (size_t i = 0; i < result.size(); i++) {
        if (i > 0) std::cout << ",";
        std::cout << result[i];
    }
    std::cout << "]";
    return 0;
}`,
      java: `import java.util.Scanner;
import java.util.ArrayList;

public class Solution {
    public static void main(String[] args) {
        Scanner scanner = new Scanner(System.in);
        
        // Parse input array
        String line = scanner.nextLine();
        String[] parts = line.substring(1, line.length()-1).split(",");
        int[] temperatures = new int[parts.length];
        for (int i = 0; i < parts.length; i++) {
            temperatures[i] = Integer.parseInt(parts[i].trim());
        }
        
        // Get result and format output
        int[] result = dailyTemperatures(temperatures);
        System.out.print("[");
        for (int i = 0; i < result.length; i++) {
            if (i > 0) System.out.print(",");
            System.out.print(result[i]);
        }
        System.out.print("]");
    }
}`
    },
    starterCode: {
      python: `def dailyTemperatures(temperatures):
    # Write your code here
    pass`,
      javascript: `function dailyTemperatures(temperatures) {
    // Write your code here
};`,
      cpp: `vector<int> dailyTemperatures(vector<int>& temperatures) {
    // Write your code here
}`,
      java: `public int[] dailyTemperatures(int[] temperatures) {
    // Write your code here
}`
    },
    testCases: [
      {
        input: '[73,74,75,71,69,72,76,73]',
        output: '[1,1,4,2,1,1,0,0]'
      }
    ]
  },
  {
    id: 'queue-2',
    title: 'Sliding Window Maximum',
    difficulty: 'Hard',
    description: `You are given an array of integers \`nums\`, there is a sliding window of size \`k\` which is moving from the very left of the array to the very right. You can only see the \`k\` numbers in the window. Each time the sliding window moves right by one position.

Return the max sliding window.`,
    examples: [
      {
        input: 'nums = [1,3,-1,-3,5,3,6,7], k = 3',
        output: '[3,3,5,5,6,7]',
        explanation: `
Window position                Max
---------------               -----
[1  3  -1] -3  5  3  6  7       3
 1 [3  -1  -3] 5  3  6  7       3
 1  3 [-1  -3  5] 3  6  7       5
 1  3  -1 [-3  5  3] 6  7       5
 1  3  -1  -3 [5  3  6] 7       6
 1  3  -1  -3  5 [3  6  7]      7`
      }
    ],
    boilerplateCode: {
      python: `import sys
import json

# Parse input
input_data = sys.stdin.read().strip().split('\n')
nums = json.loads(input_data[0])
k = int(input_data[1])

# Get result and format output
result = maxSlidingWindow(nums, k)
print(json.dumps(result))`,
      javascript: `// Parse input
const input = require('fs').readFileSync(0, 'utf-8').trim().split('\n');
const nums = JSON.parse(input[0]);
const k = parseInt(input[1]);

// Get result and format output
const result = maxSlidingWindow(nums, k);
console.log(JSON.stringify(result));`,
      cpp: `#include <iostream>
#include <string>
#include <vector>
#include <sstream>
#include <iomanip>

std::vector<int> parseArray(const std::string& input) {
    std::vector<int> result;
    std::stringstream ss(input.substr(1, input.size()-2));
    std::string item;
    while (std::getline(ss, item, ',')) {
        result.push_back(std::stoi(item));
    }
    return result;
}

int main() {
    std::string line1, line2;
    std::getline(std::cin, line1);
    std::getline(std::cin, line2);
    
    auto nums = parseArray(line1);
    int k = std::stoi(line2);
    
    auto result = maxSlidingWindow(nums, k);
    
    std::cout << "[";
    for (size_t i = 0; i < result.size(); i++) {
        if (i > 0) std::cout << ",";
        std::cout << result[i];
    }
    std::cout << "]";
    return 0;
}`,
      java: `import java.util.Scanner;
import java.util.ArrayList;

public class Solution {
    public static void main(String[] args) {
        Scanner scanner = new Scanner(System.in);
        
        // Parse input array
        String line1 = scanner.nextLine();
        String[] parts = line1.substring(1, line1.length()-1).split(",");
        int[] nums = new int[parts.length];
        for (int i = 0; i < parts.length; i++) {
            nums[i] = Integer.parseInt(parts[i].trim());
        }
        
        // Parse k
        int k = Integer.parseInt(scanner.nextLine());
        
        // Get result and format output
        int[] result = maxSlidingWindow(nums, k);
        System.out.print("[");
        for (int i = 0; i < result.length; i++) {
            if (i > 0) System.out.print(",");
            System.out.print(result[i]);
        }
        System.out.print("]");
    }
}`
    },
    starterCode: {
      python: `def maxSlidingWindow(nums, k):
    # Write your code here
    pass`,
      javascript: `function maxSlidingWindow(nums, k) {
    // Write your code here
};`,
      cpp: `vector<int> maxSlidingWindow(vector<int>& nums, int k) {
    // Write your code here
}`,
      java: `public int[] maxSlidingWindow(int[] nums, int k) {
    // Write your code here
}`
    },
    testCases: [
      {
        input: '[1,3,-1,-3,5,3,6,7]\n3',
        output: '[3,3,5,5,6,7]'
      }
    ]
  }
];
